import { loadCustomerPrivacy } from "@theme/customer-privacy";

/**
 * Reopens the Shopify cookie preferences dialog from the footer control.
 * The first-visit bar does not return after a choice; this calls the
 * banner API Shopify injects with the native cookie banner.
 *
 * Shopify injects that API for the whole shop once the banner is enabled.
 * The button stays hidden unless this visitor is in a banner region
 * (`shouldShowBanner()`) and `showPreferences` exists. US opt-out regions
 * leave the button out of the footer. Shops with the banner turned off
 * never receive privacyBanner, so the control is removed.
 * The footer nav starts hidden when it has no menu links, and is revealed
 * with the button so an empty landmark is not left behind.
 */

// Shopify lists Accept before Decline. Flex order then paints Decline first,
// so the keyboard reaches Accept while Decline is still visually next.
const placeDeclineBeforeAccept = () => {
  const decline = document.getElementById("shopify-pc__banner__btn-decline");
  const accept = document.getElementById("shopify-pc__banner__btn-accept");
  if (!(decline instanceof HTMLButtonElement)) return;
  if (!(accept instanceof HTMLButtonElement)) return;
  if (decline.nextElementSibling === accept) return;

  accept.before(decline);
};

const watchBannerOrder = () => {
  placeDeclineBeforeAccept();

  const banner = document.getElementById("shopify-pc__banner");
  if (!(banner instanceof HTMLElement)) return false;

  const observer = new MutationObserver(placeDeclineBeforeAccept);
  observer.observe(banner, { childList: true, subtree: true });
  return true;
};

const bannerApi = () => {
  const banner = window.privacyBanner;
  if (typeof banner?.showPreferences !== "function") return null;

  return banner;
};

const preferencesButton = () => {
  const button = document.querySelector("[data-cookie-preferences]");
  if (!(button instanceof HTMLButtonElement)) return null;

  return button;
};

const whenBannerApiReady = () =>
  new Promise((resolve) => {
    if (bannerApi()) {
      resolve(bannerApi());
      return;
    }

    if (document.readyState === "complete") {
      resolve(null);
      return;
    }

    window.addEventListener("load", () => resolve(bannerApi()), { once: true });
  });

const settleCookiePreferences = async () => {
  const button = preferencesButton();
  if (!button) return;

  const [privacy, banner] = await Promise.all([
    loadCustomerPrivacy(),
    whenBannerApiReady(),
  ]);

  if (
    typeof privacy?.shouldShowBanner !== "function" ||
    privacy.shouldShowBanner() !== true ||
    !banner
  ) {
    button.remove();
    return;
  }

  button.hidden = false;
  const nav = button.closest("nav");
  if (nav) nav.hidden = false;
  button.addEventListener("click", () => {
    bannerApi()?.showPreferences();
  });
};

const start = async () => {
  void settleCookiePreferences();

  if (watchBannerOrder()) return;

  const privacy = await loadCustomerPrivacy();
  if (
    typeof privacy?.shouldShowBanner !== "function" ||
    privacy.shouldShowBanner() !== true
  )
    return;

  // The first-visit bar does not return after a choice. shouldShowBanner()
  // stays true in a banner region, so a later page would watch the document
  // forever if we only checked that.
  const choice = privacy.currentVisitorConsent?.()?.analytics;
  if (choice === "yes" || choice === "no") return;
  if (watchBannerOrder()) return;

  const observer = new MutationObserver(() => {
    if (!watchBannerOrder()) return;

    observer.disconnect();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
} else {
  start();
}
